/**
 * Core music data types for notation representation
 *
 * This module contains the fundamental types for representing musical notation
 * including notes, rests, measures, and complete music data structures.
 */

import { VexFlowDuration } from "./duration";

import type { PartData, ScorePassthroughData } from "./part";

// ============================================================================
// Basic Music Types
// ============================================================================

/**
 * Pitch notation in VexFlow format (e.g., "C/4", "A/5")
 */
export type Pitch = string;

/**
 * Accidental type for notes
 */
export type Accidental = "#" | "b" | "n" | "##" | "bb";

/**
 * Tie type for notes
 */
export type TieType = "start" | "stop" | "continue";

/**
 * Clef type for multi-staff notation
 */
export type ClefType = "treble" | "bass" | "alto" | "tenor";

// ============================================================================
// Signatures and Layout
// ============================================================================

/**
 * Time signature
 */
export interface TimeSignature {
  beats: number;
  beatValue: number;
  symbol?: "common" | "cut";
}

/**
 * Key signature
 * Uses the "fifths" notation from MusicXML:
 * - Positive numbers = sharps (1=G major, 2=D major, 3=A major, etc.)
 * - Negative numbers = flats (-1=F major, -2=Bb major, etc.)
 * - 0 = C major / A minor (no sharps or flats)
 */
export interface KeySignature {
  fifths: number;
  /** "major" or "minor". Omitting defaults to major in MusicXML. */
  mode?: "major" | "minor";
}

/**
 * Clef information for multi-staff notation
 */
export interface ClefInfo {
  staffNumber: number;
  type: ClefType;
}

/**
 * Per-measure attribute overrides for mid-piece changes
 * (time signature changes, key changes, clef changes).
 * Only fields that actually changed are present; absent fields mean "no change".
 */
export interface MeasureAttributes {
  timeSignature?: TimeSignature;
  keySignature?: KeySignature;
  clefs?: ClefInfo[];
  staves?: number;
}

// ============================================================================
// Page Layout Types
// ============================================================================

/**
 * Page layout information from MusicXML
 */
export interface PageLayout {
  pageHeight: number; // Page height in tenths
  pageWidth: number; // Page width in tenths
  margins: {
    left: number;
    right: number;
    top: number;
    bottom: number;
  };
}

/**
 * Scaling information for converting tenths to pixels
 */
export interface Scaling {
  millimeters: number; // Millimeters per scaling unit
  tenths: number; // Tenths per scaling unit
}

/**
 * System layout information
 */
export interface SystemLayout {
  leftMargin?: number;
  rightMargin?: number;
  systemDistance?: number; // Distance to previous system
  topSystemDistance?: number; // Distance from top of page (first system only)
}

/**
 * Print information for measure breaks
 */
export interface PrintInfo {
  newSystem?: boolean; // Start a new system
  newPage?: boolean; // Start a new page
  systemLayout?: SystemLayout;
  staffDistance?: number; // Distance between staves in a grand staff
}

// ============================================================================
// Direction and Expression Types
// ============================================================================

/**
 * Direction element for tempo, dynamics, and text
 */
export interface Direction {
  /**
   * `number-train-color` carries no text: it is the color of the measure's
   * Number Train bar in Song Slide, held per measure because a song alternates
   * bar colors from one measure to the next. It rides on `Direction` so it
   * lands in the same per-measure array everything else measure-scoped uses,
   * and so it round-trips through MusicXML as an `<other-direction>`.
   */
  type: "words" | "metronome" | "dynamics" | "rehearsal" | "number-train-color";
  color?: string; // Hex color for rehearsal marks and Number Train bars (e.g. "#FFFF00")
  text?: string; // For words and dynamics
  tempo?: number; // For metronome (BPM)
  beatUnit?: string; // For metronome (e.g., "quarter")
  placement?: "above" | "below";
  position?: {
    defaultX?: number;
    defaultY?: number;
    relativeX?: number;
    relativeY?: number;
  };
  fontStyle?: string; // e.g., "italic"
}

/**
 * Credit element for title, composer, etc.
 */
export interface Credit {
  page: number; // Page number where credit appears
  words: string[]; // Array of text lines
  position?: {
    defaultX?: number;
    defaultY?: number;
  };
  justify?: "left" | "center" | "right";
  valign?: "top" | "middle" | "bottom";
  fontSize?: number;
}

/**
 * Barline information
 */
export interface Barline {
  location: "left" | "right" | "middle";
  barStyle:
    | "regular"
    | "dotted"
    | "dashed"
    | "heavy"
    | "light-light"
    | "light-heavy"
    | "heavy-light"
    | "heavy-heavy"
    | "tick"
    | "short"
    | "none";
  repeat?: "forward" | "backward";
}

// ============================================================================
// Note and Rest Types
// ============================================================================

/**
 * How a syllable sits inside its word, mirroring MusicXML's <syllabic>.
 * "begin" and "middle" are followed by a hyphen when rendered.
 */
export type Syllabic = "single" | "begin" | "middle" | "end";

/**
 * One syllable attached to one note, in one verse.
 *
 * `extend` marks a melisma: the syllable is held across the following notes and
 * is drawn with an extender rule rather than repeating the text.
 */
export interface Lyric {
  verse: number; // 1-based verse number (MusicXML <lyric number>)
  text: string;
  syllabic: Syllabic;
  extend?: boolean;
  /**
   * Teacher-assigned color, `#RRGGBB` uppercase. Absent when unset — never an
   * empty string — so an uncolored syllable stays byte-identical to before.
   */
  color?: string;
}

/**
 * Musical note with pitch and duration
 */
export interface Note {
  type: "note";
  pitch: Pitch;
  duration: VexFlowDuration;
  isChord: boolean;
  accidental?: Accidental;
  tied?: TieType;
  staff?: number; // Staff number for multi-staff notation (1-based, defaults to 1)
  voice?: number; // Voice number for multi-voice notation (1-based)
  chordSymbol?: string; // Chord annotation (e.g., "Cm7", "Gmaj7", "G/D")
  lyrics?: Lyric[]; // Syllables for vocal parts, at most one per verse
  /**
   * Number Train digit printed under the note, "0"–"9".
   *
   * Assigned by the teacher and never derived from the key signature — the whole
   * point of the method is that any note can carry any number. Held as a string
   * rather than a number so that "0" stays truthy and no `if (note.numberTrain)`
   * check anywhere in the codebase can silently drop a zero.
   */
  numberTrain?: string;
  /**
   * Whether this note is palm muted, which Level 1 draws as an arrow swooping
   * beneath the note's bar.
   *
   * `true` or absent, never `false`: a note that is not muted simply does not
   * carry the field, so an unmuted score serializes exactly as it did before
   * palm mutes existed. Level 1 artwork is the only place it shows today —
   * Levels 3–5, playback and MusicXML do not yet read it.
   */
  palmMute?: true;
  /**
   * @deprecated Legacy whole-measure lyric string, kept only so projects saved
   * before per-note syllables still load. `migrateLyrics` distributes it across
   * the measure's notes and deletes the field; nothing else should read it.
   */
  lyric?: string;
}

/**
 * Musical rest
 */
export interface Rest {
  type: "rest";
  duration: VexFlowDuration;
  isChord: false; // Rests cannot be part of chords
  staff?: number; // Staff number for multi-staff notation (1-based, defaults to 1)
  voice?: number; // Voice number for multi-voice notation (1-based)
}

/**
 * Union type for note or rest
 */
export type NoteOrRest = Note | Rest;

/**
 * A measure containing notes and rests
 */
export type Measure = NoteOrRest[];

// ============================================================================
// Pass-Through Data (preserves unsupported MusicXML elements)
// ============================================================================

/**
 * Stores raw XML strings for MusicXML elements we don't model in our type system.
 * This allows unsupported features (articulations, slurs, grace notes, etc.)
 * to survive import → edit → export round-trips without data loss.
 *
 * All values are raw XML strings serialized via XMLSerializer.
 * Records use string keys for JSON-serializable IndexedDB storage.
 */
export interface PassthroughData {
  /** Score-level elements (e.g., <identification>, <work>, <movement-title>) */
  scoreHeader?: string[];
  /** Defaults-level elements beyond what we model (e.g., fonts, appearance) */
  defaults?: string[];
  /** Per-measure passthrough: key is measure index as string */
  measureElements?: Record<string, string[]>;
  /** Per-note passthrough: key is "measureIdx:noteIdx" */
  noteElements?: Record<string, string[]>;
  /** Part-list extras beyond score-part elements */
  partList?: string[];
  /** Attribute elements we don't parse (e.g., transpose, staff-details): key is measure index */
  attributeElements?: Record<string, string[]>;
}

// ============================================================================
// Instrument Info (lightweight reference stored on MusicData)
// ============================================================================

/**
 * Instrument transposition (MusicXML <transpose> semantics).
 * Values represent the offset from written pitch to concert pitch.
 */
export interface Transposition {
  diatonic: number;
  chromatic: number;
  octaveChange?: number;
}

/** Instrument family for score grouping (brackets, spacing). */
export type InstrumentFamily =
  | "keyboard"
  | "woodwind"
  | "brass"
  | "string"
  | "vocal"
  | "guitar"
  | "percussion";

/**
 * Lightweight instrument metadata stored on MusicData.
 * References a full InstrumentDefinition from the catalog by `id`.
 */
export interface InstrumentInfo {
  id: string;
  name: string;
  abbreviation: string;
  midiProgram: number;
  midiChannel?: number;
  transposition?: Transposition;
  family?: InstrumentFamily;
}

// ============================================================================
// Complete Music Data Structure
// ============================================================================

/**
 * Complete music data structure.
 *
 * Multi-part: per-part data (measures, staves, clefs, attributeChanges, instrument)
 * lives inside `parts[]`. Score-level fields (timeSignature, title, etc.) remain here.
 */
export interface MusicData {
  parts: PartData[];
  timeSignature: TimeSignature;
  keySignature?: KeySignature;
  pageLayout?: PageLayout;
  scaling?: Scaling;
  /** @deprecated For multi-part scores, use PartData.printInfo instead. Kept for legacy single-part compat. */
  printInfo?: PrintInfo[];
  /** @deprecated For multi-part scores, use PartData.directions instead. Kept for legacy single-part compat. */
  directions?: Direction[][];
  credits?: Credit[];
  /** @deprecated For multi-part scores, use PartData.barlines instead. Kept for legacy single-part compat. */
  barlines?: Barline[][];
  passthrough?: ScorePassthroughData; // Score-level passthrough (per-part passthrough in PartData)
  // Editable metadata fields
  title?: string;
  subtitle?: string;
  composer?: string;
}

// ============================================================================
// Result and Error Types
// ============================================================================

/**
 * Result type for operations that can fail
 */
export type Result<T, E = Error> = { success: true; data: T } | { success: false; error: E };

/**
 * Severity of a validation issue.
 * - `error`   — the data is invalid and should be rejected.
 * - `warning` — the data is allowed but questionable (e.g. an extreme octave);
 *               surfaced to callers but never blocks an edit.
 */
export type ValidationSeverity = "error" | "warning";

/**
 * Validation error (or warning).
 *
 * `severity` is optional for backward compatibility; absent means `"error"`.
 */
export interface ValidationError {
  field: string;
  message: string;
  severity?: ValidationSeverity;
}

// ============================================================================
// Type Guards
// ============================================================================

/**
 * Check if a NoteOrRest is a Note
 */
export function isNote(noteOrRest: NoteOrRest): noteOrRest is Note {
  return noteOrRest.type === "note";
}

/**
 * Check if a NoteOrRest is a Rest
 */
export function isRest(noteOrRest: NoteOrRest): noteOrRest is Rest {
  return noteOrRest.type === "rest";
}

// ============================================================================
// Factory Functions
// ============================================================================

/**
 * Create a new note
 */
export function createNote(
  pitch: Pitch,
  duration: VexFlowDuration,
  isChord: boolean = false,
  staff?: number,
  accidental?: Accidental,
  tied?: TieType,
  voice?: number
): Note {
  const note: Note = {
    type: "note",
    pitch,
    duration,
    isChord,
  };
  if (staff !== undefined) {
    note.staff = staff;
  }
  if (accidental !== undefined) {
    note.accidental = accidental;
  }
  if (tied !== undefined) {
    note.tied = tied;
  }
  if (voice !== undefined) {
    note.voice = voice;
  }
  return note;
}

/**
 * Create a new rest
 */
export function createRest(duration: VexFlowDuration, staff?: number, voice?: number): Rest {
  const rest: Rest = {
    type: "rest",
    duration,
    isChord: false,
  };
  if (staff !== undefined) {
    rest.staff = staff;
  }
  if (voice !== undefined) {
    rest.voice = voice;
  }
  return rest;
}

/**
 * Create an empty measure with rests
 */
export function createEmptyMeasure(noteCount: number, duration: VexFlowDuration = "q"): Measure {
  return Array(noteCount)
    .fill(null)
    .map(() => createRest(duration));
}

/**
 * Create default music data (multi-part format with a single Piano part).
 *
 * The piano is initialized as a grand staff (staves=2) with treble and bass clefs.
 * Each measure contains `beatsPerMeasure` quarter rests on staff 1 followed by the
 * same count on staff 2 — matching the time signature so the beat count is always
 * derived from the signature rather than being set independently.
 */
export function createDefaultMusicData(
  measureCount: number = 4,
  beatsPerMeasure: number = 4,
  options?: { preset?: "song-slide" }
): MusicData {
  const defaultTimeSignature: TimeSignature = { beats: beatsPerMeasure, beatValue: 4 };

  const buildMeasure = (): Measure => [
    ...Array(beatsPerMeasure)
      .fill(null)
      .map(() => createRest("q", 1)),
    ...Array(beatsPerMeasure)
      .fill(null)
      .map(() => createRest("q", 2)),
  ];

  if (options?.preset === "song-slide") {
    const buildSingleStaveMeasure = (): Measure =>
      Array(beatsPerMeasure)
        .fill(null)
        .map(() => createRest("q", 1));

    return {
      parts: [
        {
          id: "P1",
          instrument: { id: "harmony", name: "Harmony", abbreviation: "Har.", midiProgram: 0 },
          measures: Array(measureCount).fill(null).map(buildSingleStaveMeasure),
          staves: 1,
          clefs: [{ staffNumber: 1, type: "treble" as ClefType }],
        },
        {
          id: "P2",
          instrument: { id: "lead", name: "Lead", abbreviation: "Ld.", midiProgram: 0 },
          measures: Array(measureCount).fill(null).map(buildSingleStaveMeasure),
          staves: 1,
          clefs: [{ staffNumber: 1, type: "treble" as ClefType }],
        },
        {
          id: "P3",
          instrument: { id: "bass", name: "Bass", abbreviation: "Bs.", midiProgram: 32 },
          measures: Array(measureCount).fill(null).map(buildSingleStaveMeasure),
          staves: 1,
          clefs: [{ staffNumber: 1, type: "bass" as ClefType }],
        },
        {
          id: "P4",
          instrument: {
            id: "vocals",
            name: "Vocals",
            abbreviation: "Voc.",
            midiProgram: 52,
            family: "vocal",
          },
          measures: Array(measureCount).fill(null).map(buildSingleStaveMeasure),
          staves: 1,
          clefs: [{ staffNumber: 1, type: "treble" as ClefType }],
        },
      ],
      timeSignature: defaultTimeSignature,
    };
  }

  return {
    parts: [
      {
        id: "P1",
        instrument: {
          id: "piano",
          name: "Piano",
          abbreviation: "Pno.",
          midiProgram: 0,
        },
        measures: Array(measureCount).fill(null).map(buildMeasure),
        staves: 2,
        clefs: [
          { staffNumber: 1, type: "treble" as ClefType },
          { staffNumber: 2, type: "bass" as ClefType },
        ],
      },
    ],
    timeSignature: defaultTimeSignature,
  };
}

// ============================================================================
// Pitch Utilities
// ============================================================================

/**
 * Parse a pitch string into step and octave
 */
export function parsePitch(pitch: Pitch): { step: string; octave: number } | null {
  const match = pitch.match(/^([A-G])\/(\d)$/);
  if (!match) return null;

  return {
    step: match[1],
    octave: parseInt(match[2]),
  };
}

/**
 * Create a pitch string from step and octave
 */
export function createPitchString(step: string, octave: number): Pitch {
  return `${step}/${octave}`;
}

/**
 * Validate a pitch string
 */
export function isValidPitch(pitch: string): boolean {
  return /^[A-G]\/\d$/.test(pitch);
}
